import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { useFetch, Panel, Tag, PageHeader } from "@/components/Common";
import { api, errMsg, fmtTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

function CredRow({ c, reload }) {
  const [v, setV] = useState("");
  const save = async () => { try { await api.put(`/credentials/${c.key}`, { value: v }); setV(""); toast.success(`${c.label} saved (encrypted)`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { try { await api.delete(`/credentials/${c.key}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const id = c.key.replace(/_/g, "-");
  return (
    <div className="grid items-center gap-3 border-b border-border/50 py-4 lg:grid-cols-[1.2fr_1fr_auto]" data-testid={`credential-row-${id}`}>
      <div>
        <div className="flex items-center gap-2"><span className="font-medium">{c.label}</span><Tag>{c.group}</Tag><Tag tone={c.configured ? "up" : "muted"} testid={`credential-status-${id}`}>{c.configured ? "configured" : "not set"}</Tag></div>
        <div className={`mt-1 text-xs ${c.cost.startsWith("PAID") ? "text-[hsl(var(--warn))]" : "text-muted-foreground"}`}>{c.cost}</div>
        {c.configured && <div className="mt-1 font-mono text-xs text-muted-foreground">{c.masked} · updated {fmtTime(c.updated_at)}</div>}
      </div>
      <Input type="password" autoComplete="off" placeholder={c.configured ? "Replace value" : "Paste value"} value={v} onChange={(e) => setV(e.target.value)} data-testid={`api-key-${id}-input`} />
      <div className="flex gap-2">
        <Button onClick={save} disabled={v.length < 4} data-testid={`api-key-${id}-save`}>Save</Button>
        {c.configured && <Button variant="ghost" onClick={del} data-testid={`api-key-${id}-delete`}><Trash2 size={14} /></Button>}
      </div>
    </div>
  );
}

function Notifications() {
  const { data, reload } = useFetch("/notifications");
  if (!data) return null;
  const cfg = data.config;
  const save = async (upd) => { try { await api.put("/notifications", upd); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const test = async () => { try { const { data: r } = await api.post("/notifications/test"); toast.success(`Email: ${r.email} · Telegram: ${r.telegram}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Notifications" testid="notifications-panel" right={<Button size="sm" onClick={test} data-testid="notifications-test-button">Send test</Button>}>
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2"><Switch checked={cfg.email_enabled} onCheckedChange={(v) => save({ email_enabled: v })} data-testid="notify-email-switch" />Email to Owner (managed)</label>
        <label className="flex items-center gap-2"><Switch checked={cfg.telegram_enabled} onCheckedChange={(v) => save({ telegram_enabled: v })} data-testid="notify-telegram-switch" />Telegram (needs bot token + chat ID)</label>
      </div>
      <div className="mt-4 space-y-1">{data.log.slice(0, 8).map((n) => <div key={n.id} className="font-mono text-xs text-muted-foreground">{fmtTime(n.at)} · {n.event} · {n.subject} · email {n.email} · tg {n.telegram}</div>)}</div>
    </Panel>
  );
}

function AccountSecurity() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    if (newPassword.length < 10) { toast.error("Use at least 10 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success("Password changed. Use your new password for the next sign-in.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Account security" testid="account-security-panel" right={<Tag tone="blue">MFA required</Tag>}>
      <p className="mb-4 text-sm text-muted-foreground">Signed in as <span className="font-mono text-foreground">{user.email}</span>. Update this account's password below.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="space-y-1 text-sm">Current password<Input type="password" autoComplete="current-password" required disabled={busy} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="current-password-input" /></label>
          <label className="space-y-1 text-sm">New password<Input type="password" autoComplete="new-password" minLength={10} required disabled={busy} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" /></label>
          <label className="space-y-1 text-sm">Confirm new password<Input type="password" autoComplete="new-password" minLength={10} required disabled={busy} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" /></label>
        </div>
        <Button type="submit" disabled={busy || !currentPassword || newPassword.length < 10 || newPassword !== confirmPassword} data-testid="change-password-button">{busy ? "Updating…" : "Change password"}</Button>
      </form>
    </Panel>
  );
}

export function DesktopReader() {
  const { data, error, updatedAt } = useFetch('/local-mt5/status', [], 2000);
  const [clock,setClock] = useState(Date.now());
  useEffect(()=>{
    const current=Date.now();setClock(current);
    const remaining=(5-data?.receipt_age_seconds)*1000-Math.max(0,current-Date.parse(updatedAt));
    if(!Number.isFinite(remaining)) return;
    const timer=setTimeout(()=>setClock(Date.now()),Math.max(0,remaining));
    return()=>clearTimeout(timer);
  },[data,updatedAt]);
  const measured=Date.parse(updatedAt), age=data?.receipt_age_seconds;
  const fresh=!error && data?.connected===true && data?.read_only===true && data?.trade_ready===false
    && typeof age==='number' && Number.isFinite(age) && age>=0 && Number.isFinite(measured)
    && clock>=measured-1000 && age+Math.max(0,Math.max(clock,Date.now())-measured)/1000<5;
  const [pairing, setPairing] = useState(null);
  const [busy, setBusy] = useState(false);
  const pair = async () => {
    if (!window.confirm('Create a read-only demo reader key valid until you revoke or replace it? Any previous reader key will stop working. This does not enable trading.')) return;
    setBusy(true);
    try { const r = await api.post('/local-mt5/pair'); setPairing(r.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm('Revoke the demo reader key? Its data transfer will stop. No broker position will be changed.')) return;
    try { await api.delete('/local-mt5/pair'); setPairing(null); toast.success('Reader key revoked'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copyKey = async () => {
    if (!pairing?.token) return;
    try {
      await navigator.clipboard.writeText(pairing.token);
      toast.success('Reader key copied. Paste only into the cloud reader masked input.');
    } catch {
      toast.error('Clipboard copy was blocked by the browser. Key has not been copied.');
    }
  };
  return (
    <Panel title="Desktop MT5 — connection test" testid="desktop-mt5-panel" right={<Tag tone="warn">Trading OFF · read only</Tag>}>
      <p className="text-sm text-muted-foreground">Direct MetaQuotes-Demo terminal data, without MetaApi. Run the reader and MT5 on your cloud Windows server; your laptop can be off. This diagnostic feed does not yet drive the trading engine or auto-start after a server reboot.</p>
      <div className="my-3 flex flex-wrap items-center gap-3">
        <Tag tone={fresh ? 'up' : 'down'}>{fresh ? 'Reader heartbeat received' : 'Reader not connected'}</Tag>
        {data?.received_at && <span className="font-mono text-xs">Receipt UTC: {fmtTime(data.received_at)} · {data.receipt_age_seconds}s ago</span>}
        <Button size="sm" disabled={busy || !!pairing} onClick={pair} data-testid="desktop-mt5-pair">{busy ? 'Creating…' : 'Create read-only pairing key'}</Button>
        <a className="text-xs underline" href="/api/local-mt5/download">Download Windows reader source</a>
        <Button size="sm" variant="outline" onClick={revoke}>Revoke reader key</Button>
      </div>
      {pairing && <div className="mb-4 space-y-2 rounded border p-3">
        <p className="text-xs">Copy this key into the reader's masked input. Do not send it in chat. It stays valid until you revoke or replace it, but is shown only on this page. Existing 24-hour keys keep their original expiry; create a new key to use the new lifetime.</p>
        <Input type="password" readOnly autoComplete="off" value={pairing.token} aria-label="Desktop reader pairing key" />
        <Button size="sm" onClick={copyKey} data-testid="desktop-mt5-copy-key">Copy key</Button>
        <p className="text-xs text-muted-foreground">Use Copy key, not Ctrl+C on the masked field. Wait for the copied confirmation, then paste into the cloud reader. This key is not a MetaApi token.</p>
        <Button size="sm" variant="outline" onClick={() => setPairing(null)}>Hide key</Button>
      </div>}
      {error && <p className="text-xs text-[hsl(var(--warn))]">Diagnostic endpoint unavailable; no desktop data connected.</p>}
      <p className="text-xs text-[hsl(var(--warn))]">Broker timestamps are raw and not yet verified as UTC. A receipt heartbeat is not proof of a fresh market tick.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(fresh ? data?.quotes || {} : {}).map(([symbol, q]) => <div key={symbol} className="rounded border p-2 font-mono text-xs">
          <div>{symbol} · {q.bid} / {q.ask}</div>
          <div className="text-muted-foreground">Observed tick changes: {q.observed_tick_changes} · last change {q.seconds_since_observed_change == null ? 'not observed' : `${q.seconds_since_observed_change.toFixed(1)}s ago`}</div>
        </div>)}
      </div>
    </Panel>
  );
}

export default function Settings() {
  const { data, reload } = useFetch("/credentials");
  const { user } = useAuth();
  return (
    <div className="space-y-4">
      <PageHeader title="Credentials" sub="API keys are encrypted at rest (Fernet / AES) and never returned to the browser" />
      <AccountSecurity />
      {user.role === "owner" && <DesktopReader />}
      {user.role === "owner" && <DesktopExecutionEnrollment />}
      {user.role === "owner" && <ExecutorVerification />}
      <Panel title="Integrations" right={<KeyRound size={16} className="text-gold" />}>
        {data?.map((c) => <CredRow key={c.key} c={c} reload={reload} />)}
        <p className="mt-4 text-xs text-muted-foreground">Always-on free sources (no key): Yahoo Finance market data fallback (unofficial), Forex Factory weekly calendar export, Fed / ECB / BoE / BoJ official releases, Investing.com / CNBC / FXStreet RSS news. AI analysis uses Claude via the Emergent Universal Key.</p>
      </Panel>
      {user.role === "owner" && <Notifications />}
    </div>
  );
}

function ExecutorVerification() {
  const { data, reload } = useFetch('/local-mt5/execution/inspection', [], 5000);
  const [referenceTicket, setReferenceTicket] = useState('');
  const [referenceTime, setReferenceTime] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const review = async () => {
    if (!window.confirm('Confirm you independently compared the MT5 broker clock with Windows UTC and the reference position time in MT5. This verifies demo clocks and broker-history risk baselines only; it does NOT arm the bot or place an order.')) return;
    setReviewing(true);
    try {
      await api.post('/local-mt5/execution/review-clocks', {
        quote_offset_seconds: Number(data.quote_clock_offset_asserted),
        position_ticket: Number(referenceTicket), observed_broker_opened_at: referenceTime,
        independent_quote_clock_reviewed: true
      });
      toast.success('Demo clocks and broker-history baseline reviewed. Trading remains unarmed.'); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setReviewing(false); }
  };
  const frames = Object.values(data?.closed_bar_frames || {}).reduce((n, x) => n + Object.keys(x).length, 0);
  return <Panel title="Demo execution verification" testid="demo-executor-verification" right={<Tag tone={data?.connected ? 'up' : 'warn'}>{data?.connected ? 'Telemetry connected' : 'Telemetry not verified'}</Tag>}>
    <p className="text-sm text-muted-foreground">Connection is not trading permission. The first broker test still requires verified quote/deal clocks and account risk baselines. Real execution remains forbidden.</p>
    <div className="mt-3 text-xs space-y-1">
      <div>Quotes: {Object.keys(data?.quotes || {}).length}/7 · Closed history frames: {frames}/42 · Positions: {data?.positions_count ?? '—'} · Broker deals: {data?.deals_count ?? '—'}</div>
      <div>Independent quote clock: {data?.quote_clock_verified ? 'verified' : 'missing'} · Deal-history clock: {data?.history_clock_verified ? 'verified' : 'missing'}</div>
      <div>Risk baseline: {data?.broker_baseline_present ? 'present' : 'missing'} · Broker open/modify/close: {data?.broker_chain_verified ? 'verified' : 'not completed'}</div>
      <div>Last authenticated packet: {fmtTime(data?.received_at)} · receipt age {data?.receipt_age_seconds ?? '—'}s</div>
    </div>
    <ul className="my-3 list-inside list-disc text-xs text-muted-foreground">{(data?.blockers || []).map(b => <li key={b}>{b}</li>)}</ul>
    {!data?.broker_chain_verified && <div className="mt-3 space-y-2 border-t pt-3">
      <p className="text-xs text-muted-foreground">Independent owner clock review: read the reference position ticket and its broker-display opening time directly from MT5. Do not label broker wall time as UTC. This does not grant management permission for that reference position.</p>
      <div className="flex flex-wrap gap-2">
        <Input value={referenceTicket} onChange={e => setReferenceTicket(e.target.value)} placeholder="MT5 position ticket" className="max-w-48" aria-label="Reference MT5 position ticket" />
        <Input value={referenceTime} onChange={e => setReferenceTime(e.target.value)} placeholder="YYYY-MM-DDTHH:mm:ss" className="max-w-64" aria-label="Observed broker opening time" />
        <Button disabled={reviewing || !data?.connected || !/^\d+$/.test(referenceTicket) || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(referenceTime)} onClick={review}>Review demo clocks and baseline</Button>
      </div>
    </div>}
  </Panel>;
}

function DesktopExecutionEnrollment() {
  const { data, reload } = useFetch('/local-mt5/execution/status', [], 5000);
  const [pairing, setPairing] = useState(null);
  const [busy, setBusy] = useState(false);
  const pair = async () => {
    if (!window.confirm('Create a SEPARATE permanent demo-order key for MetaQuotes-Demo account 5057236498 only? Future executor code will use it to OPEN, CLOSE and MODIFY protected demo orders. Real accounts are forbidden. This key does NOT start trading and does NOT change your read-only reader key. A previous demo-order key will be replaced.')) return;
    setBusy(true);
    try { const r = await api.post('/local-mt5/execution/pair'); setPairing(r.data); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(pairing.token); toast.success('Demo-order key copied. Do not paste into the read-only reader.'); }
    catch { toast.error('Copy blocked. No key copied.'); }
  };
  const revoke = async () => {
    if (!window.confirm('Revoke only the demo-order key? The read-only price reader will remain unchanged. This does not close broker positions.')) return;
    try { await api.delete('/local-mt5/execution/pair'); setPairing(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <Panel title="Cloud demo execution — permission enrollment" testid="desktop-execution-panel" right={<Tag tone="warn">Not armed · not ready</Tag>}>
    <p className="text-sm text-muted-foreground">Fixed MetaQuotes-Demo account 5057236498 only. Separate from the working price reader. The executor package supports protected demo commands; verified clocks, portfolio baselines and the broker open/modify/close test are still required. Creating a key does not enable the bot.</p>
    <ul className="my-3 list-inside list-disc text-xs text-muted-foreground">{(data?.blockers || []).map(b => <li key={b}>{b}</li>)}</ul>
    <div className="flex flex-wrap gap-2">
      <a className="text-xs underline self-center" href="/api/local-mt5/download-executor">Download Windows demo executor</a>
      <Button size="sm" disabled={busy || !!pairing} onClick={pair} data-testid="desktop-execution-pair">Create separate demo-order key</Button>
      {data?.paired && <Button size="sm" variant="outline" onClick={revoke}>Revoke demo-order key</Button>}
    </div>
    {pairing && <div className="mt-3 space-y-2 rounded border p-3">
      <Input type="password" readOnly autoComplete="off" value={pairing.token} aria-label="Separate demo-order key" />
      <Button size="sm" onClick={copy}>Copy demo-order key</Button>
      <Button size="sm" variant="outline" onClick={() => setPairing(null)}>Hide key</Button>
      <p className="text-xs text-muted-foreground">Keep this private. Do not send it in chat or put it in MetaApi credentials. No daily expiry; valid until replaced/revoked. No cloud executor is armed by this enrollment.</p>
    </div>}
  </Panel>;
}
